import { atom, atomFamily, selector, selectorFamily } from 'recoil';
import { Color } from '../enums/Color.enum';
import { DeviceType } from '../enums/Device.enum';
import { MarketingPromo } from '../enums/MarketingPromo';
import { PageContext } from '../enums/PageContext.enum';
import { PageSubContext } from '../enums/PageSubContext.enum';
import { isProductHandle, ProductHandle } from '../enums/ProductHandle.enum';
import { SelectionType } from '../enums/SelectionType.enum';
import { SubContext } from '../enums/SubContext.enum';
import { DrawerState } from '../models/DrawerState';
import { HoneyloveAbTests } from '../models/HoneyloveAbTests';
import { SizingToolStateProps } from '../models/SizingToolState.model';
import { HoneyloveReviewSet } from '../models/HoneyloveReview.model';
import { HoneyloveEnvironmentContext, HoneyloveWindow, SkuAvailability } from '../models/HoneyloveWindow';
import { ReceiveKlaviyoResponsePayload } from '../models/klaviyo/SubscribeToKlaviyo';
import { ModalState } from '../models/ModalState';
import { Preorder } from '../models/Preorder.model';
import { Shopify } from '../models/Shopify.model';
import { ShopifyCart } from '../models/ShopifyCart.model';
import { ShopifyVariant, ViewVariant } from '../models/ShopifyVariant.model';
import { LongSize } from '../product_detail/components/size.enum';
import { DrawerToggleState, ModalToggleState, SizingToolState } from '../models/actionStates';
import { assetPathFn, getURLParam, SIZE_TO_FILTER_BY, toViewPrice } from '../util';
import { Common, Selections } from './keys';
import { ShopifyProducts } from '../models/ShopifyProduct.model';
import { AssetPathFn } from '../models/CommonFunctions';
import axios from 'axios';
import { to } from 'await-to-js';
import { Size } from '../sizing-tool/enums/size.enum';
import { Fit } from '../enums/Fit.enum';
import { ViewProducts } from '../models/ViewProduct.model';
import { toViewProducts } from './helpers/productHelper';
import { AppliedFilters, CollectionFilter } from '../collection/components/CollectionFilters';
import { NavCategorySet } from '../models/NavCategorySet.model';
import { allNavCategorySet } from '../hooks/actions/useNavCategories';
import { Denomination } from '../product_detail/components/denomination.enum';
import { CustomerViewModel } from '../models/CustomerView.model';
import { BundleCollectionHandle, CollectionHandle, isBundleCollectionHandle } from '../enums/CollectionHandle.enum';
import { ShopifyCollection } from '../models/ShopifyCollection.model';
import { bundleConfiguration } from '../hooks/bundleConfiguration';
import { isInDateRange } from '../discounts';

declare const window: HoneyloveWindow;

type SelectionTypeKey = { selectionType: SelectionType; key: string };

export const pageContextState = atom<PageContext | undefined>({
  key: Common.PageContext,
  default: typeof window !== 'undefined' ? window?.Honeylove?.pageContext : PageContext.LandingPage, // TODO: SSR set PageContext
});

export const pageSubContextState = atom<PageSubContext | undefined>({
  key: Common.PageSubContext,
  default: typeof window !== 'undefined' ? window?.Honeylove?.pageSubContext : PageSubContext.Other, // TODO: SSR set PageSubContext
});

export const allProductsState = atom<ViewProducts>({
  key: Common.AllProducts,
  default: selector({
    key: Common.AllProductsDefault,
    get: async ({ get }) => {
      const environmentContext = get(environmentContextState);
      const shopify = get(shopifyState);

      // Load the product info from the API if they use USD, with a 1 second timeout
      if (shopify?.currency.active === 'USD') {
        const [err, products] = await to(
          axios.get<ShopifyProducts>(`https://${window.Honeylove.environment.dataApiDomain}/api/shopify/products`, { timeout: 1000 }).then((r) => r.data),
        );
        if (!err && products) {
          return toViewProducts(products, shopify?.currency.active, environmentContext);
        }
      }

      // Fallback to a liquid page containing the product json data
      // Unable to use a .json file in the assets folder because Shopify doesn't allow access to product info from assets
      const [pageErr, pageProducts] = await to(axios.get(`/pages/product-data`).then((r) => r.data));
      if (pageErr) {
        return {} as ViewProducts;
      }

      const rawJson = pageProducts.substring(pageProducts.indexOf('|||START|||') + 11, pageProducts.indexOf('|||END|||'));
      const parsedJson: { products: ShopifyProducts; skuAvailability: SkuAvailability } = JSON.parse(rawJson);

      const rawProducts = Object.keys(parsedJson.products).reduce((acc, productHandle) => {
        const parsedProduct = parsedJson.products[productHandle];

        const getOptionValueByName = <T>(optionName: string, variant: ShopifyVariant): T => {
          const index = parsedProduct.options?.indexOf(optionName);
          return index !== -1 ? ((variant as unknown as { [key: string]: T })[`option${index + 1}`] as T) : (undefined as unknown as T);
        };

        if (!parsedProduct.error) {
          const rawProduct = {
            ...parsedProduct,
            variants: parsedProduct.variants.map((v) => ({
              ...v,
              fit: getOptionValueByName<Fit>('Fit', v),
              size: getOptionValueByName<Size>('Size', v),
              color: getOptionValueByName<Color>('Color', v),
              skuAvailability: parsedJson.skuAvailability[v.sku],
            })),
          };
          return { ...acc, [productHandle]: rawProduct };
        } else {
          return acc;
        }
      }, {});

      return toViewProducts(rawProducts, shopify?.currency.active ?? 'USD', environmentContext);
    },
  }),
});

interface MetaEnv {
  [key: string]: string;
}

const env: MetaEnv = typeof import.meta !== 'undefined' ? ((import.meta as any).env as unknown as MetaEnv) : ({} as MetaEnv);
const findEnvVar = (keys: string[], ssrKey: string): string | number | string[] | undefined => {
  const windowEnv: { [key: string]: any } = typeof window !== 'undefined' ? window.Honeylove.environment : {};
  const windowEnvValue = keys.reduce((acc, key) => (acc[key] ? acc[key] : acc), windowEnv) as unknown as string | number | string[] | undefined;
  return typeof window !== 'undefined' ? windowEnvValue : env[ssrKey];
};
const findEnvVarString = (keys: string[], ssrKey: string): string => (findEnvVar(keys, ssrKey) ?? '') as string;
const findEnvVarNumber = (keys: string[], ssrKey: string): number => +(findEnvVar(keys, ssrKey) ?? 0) as number;
const findEnvVarArray = (keys: string[], ssrKey: string): string[] =>
  (typeof window !== 'undefined' ? (findEnvVar(keys, ssrKey) as string[]) : (findEnvVar(keys, ssrKey) as string).split(',')) ?? [];
const findEnvVarObject = (keys: string[], ssrKey: string): { [key: string]: any } =>
  (typeof window !== 'undefined' ? (findEnvVar(keys, ssrKey) as { [key: string]: any }) : JSON.parse(findEnvVar(keys, ssrKey) as string)) ?? {};
const findEnvVarBoolean = (keys: string[], ssrKey: string): boolean => findEnvVarString(keys, ssrKey).toString().toUpperCase() === 'TRUE';
const timedSaleStartISO = findEnvVarString(['timedSaleStartISO'], 'PUBLIC_TIMED_SALE_START_ISO');
const timedSaleStopISO = findEnvVarString(['timedSaleStopISO'], 'PUBLIC_TIMED_SALE_STOP_ISO');

export const environmentContextState = atom<HoneyloveEnvironmentContext>({
  key: Common.EnvironmentContext,
  default: {
    assetUrl: findEnvVarString(['assetUrl'], 'PUBLIC_ASSET_URL'),
    domain: findEnvVarString(['domain'], 'PUBLIC_DOMAIN'),
    useNativeEmailPopups: findEnvVarBoolean(['useNativeEmailPopups'], 'PUBLIC_USE_NATIVE_EMAIL_POPUPS'),
    permanentDomain: findEnvVarString(['permanentDomain'], 'PUBLIC_PERMANENT_DOMAIN'),
    yotpoAppKey: findEnvVarString(['yotpoAppKey'], 'PUBLIC_YOTPO_APP_KEY'),
    giftCardShopId: findEnvVarNumber(['giftCardShopId'], 'PUBLIC_GIFT_CARD_SHOP_ID'),
    malomoKey: findEnvVarString(['malomoKey'], 'PUBLIC_MALOMO_KEY'),
    dataApiDomain: findEnvVarString(['dataApiDomain'], 'PUBLIC_DATA_API_DOMAIN'),
    smsListId: findEnvVarString(['smsListId'], 'PUBLIC_SMS_LIST_ID'),
    smsPhoneUS: findEnvVarString(['smsPhoneUS'], 'PUBLIC_SMS_PHONE_US'),
    smsPhoneInt: findEnvVarString(['smsPhoneInt'], 'PUBLIC_SMS_PHONE_INT'),
    verifyPassDataKey: findEnvVarString(['verifyPassDataKey'], 'PUBLIC_VERIFY_PASS_DATA_kEY'),
    klaviyoSiteId: findEnvVarString(['klaviyoSiteId'], 'PUBLIC_KLAVIYO_SITE_ID'),
    klaviyo: {
      loyaltySignup: {
        formId: findEnvVarString(['klaviyo', 'loyaltySignup', 'formId'], 'PUBLIC_KLAVIYO_LOYALTY_SIGNUP_FORM_ID'),
        listId: findEnvVarString(['klaviyo', 'loyaltySignup', 'listId'], 'PUBLIC_KLAVIYO_LOYALTY_SIGNUP_LIST_ID'),
        versionId: findEnvVarNumber(['klaviyo', 'loyaltySignup', 'versionId'], 'PUBLIC_KLAVIYO_LOYALTY_SIGNUP_VERSION_ID'),
        formName: findEnvVarString(['klaviyo', 'loyaltySignup', 'formName'], 'PUBLIC_KLAVIYO_LOYALTY_SIGNUP_FORM_NAME'),
        companyId: findEnvVarString(['klaviyoSiteId'], 'PUBLIC_KLAVIYO_SITE_ID'),
      },
      newsletterFooter: {
        formId: findEnvVarString(['klaviyo', 'newsletterFooter', 'formId'], 'PUBLIC_KLAVIYO_NEWSLETTER_FOOTER_FORM_ID'),
        listId: findEnvVarString(['klaviyo', 'newsletterFooter', 'listId'], 'PUBLIC_KLAVIYO_NEWSLETTER_FOOTER_LIST_ID'),
        versionId: findEnvVarNumber(['klaviyo', 'newsletterFooter', 'versionId'], 'PUBLIC_KLAVIYO_NEWSLETTER_FOOTER_VERSION_ID'),
        formName: findEnvVarString(['klaviyo', 'newsletterFooter', 'formName'], 'PUBLIC_KLAVIYO_NEWSLETTER_FOOTER_FORM_NAME'),
        companyId: findEnvVarString(['klaviyoSiteId'], 'PUBLIC_KLAVIYO_SITE_ID'),
      },
      newsletterDiscount: {
        formId: findEnvVarString(['klaviyo', 'newsletterDiscount', 'formId'], 'PUBLIC_KLAVIYO_NEWSLETTER_DISCOUNT_FORM_ID'),
        listId: findEnvVarString(['klaviyo', 'newsletterDiscount', 'listId'], 'PUBLIC_KLAVIYO_NEWSLETTER_DISCOUNT_LIST_ID'),
        versionId: findEnvVarNumber(['klaviyo', 'newsletterDiscount', 'versionId'], 'PUBLIC_KLAVIYO_NEWSLETTER_DISCOUNT_VERSION_ID'),
        formName: findEnvVarString(['klaviyo', 'newsletterDiscount', 'formName'], 'PUBLIC_KLAVIYO_NEWSLETTER_DISCOUNT_FORM_NAME'),
        companyId: findEnvVarString(['klaviyoSiteId'], 'PUBLIC_KLAVIYO_SITE_ID'),
      },
      newsletterIntimatesBag: {
        formId: findEnvVarString(['klaviyo', 'newsletterIntimatesBag', 'formId'], 'PUBLIC_KLAVIYO_NEWSLETTER_INTIMATES_BAG_FORM_ID'),
        listId: findEnvVarString(['klaviyo', 'newsletterIntimatesBag', 'listId'], 'PUBLIC_KLAVIYO_NEWSLETTER_INTIMATES_BAG_LIST_ID'),
        versionId: findEnvVarNumber(['klaviyo', 'newsletterIntimatesBag', 'versionId'], 'PUBLIC_KLAVIYO_NEWSLETTER_INTIMATES_BAG_VERSION_ID'),
        formName: findEnvVarString(['klaviyo', 'newsletterIntimatesBag', 'formName'], 'PUBLIC_KLAVIYO_NEWSLETTER_INTIMATES_BAG_FORM_NAME'),
        companyId: findEnvVarString(['klaviyoSiteId'], 'PUBLIC_KLAVIYO_SITE_ID'),
      },
      backInStockForm: {
        formId: findEnvVarString(['klaviyo', 'backInStockForm', 'formId'], 'PUBLIC_KLAVIYO_BACK_IN_STOCK_FORM_FORM_ID'),
        listId: findEnvVarString(['klaviyo', 'backInStockForm', 'listId'], 'PUBLIC_KLAVIYO_BACK_IN_STOCK_FORM_LIST_ID'),
        versionId: findEnvVarNumber(['klaviyo', 'backInStockForm', 'versionId'], 'PUBLIC_KLAVIYO_BACK_IN_STOCK_FORM_VERSION_ID'),
        formName: findEnvVarString(['klaviyo', 'backInStockForm', 'formName'], 'PUBLIC_KLAVIYO_BACK_IN_STOCK_FORM_FORM_NAME'),
        companyId: findEnvVarString(['klaviyoSiteId'], 'PUBLIC_KLAVIYO_SITE_ID'),
      },
      size4x5xId: {
        formId: findEnvVarString(['klaviyo', 'size4x5xId', 'formId'], 'PUBLIC_KLAVIYO_SIZE_4X_5X_ID_FORM_ID'),
        listId: findEnvVarString(['klaviyo', 'size4x5xId', 'listId'], 'PUBLIC_SIZE_4X_5X_ID_LIST_ID'),
        versionId: findEnvVarNumber(['klaviyo', 'size4x5xId', 'versionId'], 'PUBLIC_KLAVIYO_SIZE_4X_5X_ID_VERSION_ID'),
        formName: findEnvVarString(['klaviyo', 'size4x5xId', 'formName'], 'PUBLIC_KLAVIYO_SIZE_4X_5X_ID_FORM_NAME'),
        companyId: findEnvVarString(['klaviyoSiteId'], 'PUBLIC_KLAVIYO_SITE_ID'),
      },
      vip: {
        formId: findEnvVarString(['klaviyo', 'vip', 'formId'], 'PUBLIC_KLAVIYO_VIP_FORM_ID'),
        listId: findEnvVarString(['klaviyo', 'vip', 'listId'], 'PUBLIC_VIP_LIST_ID'),
        versionId: findEnvVarNumber(['klaviyo', 'vip', 'versionId'], 'PUBLIC_KLAVIYO_VIP_VERSION_ID'),
        formName: findEnvVarString(['klaviyo', 'vip', 'formName'], 'PUBLIC_KLAVIYO_VIP_FORM_NAME'),
        companyId: findEnvVarString(['klaviyoSiteId'], 'PUBLIC_KLAVIYO_SITE_ID'),
      },
      couponPage: {
        formId: findEnvVarString(['klaviyo', 'couponPage', 'formId'], 'PUBLIC_KLAVIYO_COUPON_PAGE_FORM_ID'),
        listId: findEnvVarString(['klaviyo', 'couponPage', 'listId'], 'PUBLIC_COUPON_PAGE_LIST_ID'),
        versionId: findEnvVarNumber(['klaviyo', 'couponPage', 'versionId'], 'PUBLIC_KLAVIYO_COUPON_PAGE_VERSION_ID'),
        formName: findEnvVarString(['klaviyo', 'couponPage', 'formName'], 'PUBLIC_KLAVIYO_COUPON_PAGE_FORM_NAME'),
        companyId: findEnvVarString(['klaviyoSiteId'], 'PUBLIC_KLAVIYO_SITE_ID'),
      },
    },
    clerkDomain: findEnvVarString(['clerkDomain'], 'PUBLIC_CLERK_DOMAIN'),
    loyaltyFlashSaleMultiplier: findEnvVarNumber(['loyaltyFlashSaleMultiplier'], 'PUBLIC_LOYALTY_FLASH_SALE_MULTIPLIER'),
    loyaltyFlashSaleStartISO: findEnvVarString(['loyaltyFlashSaleStartISO'], 'PUBLIC_LOYALTY_FLASH_SALE_START_ISO'),
    loyaltyFlashSaleStopISO: findEnvVarString(['loyaltyFlashSaleStopISO'], 'PUBLIC_LOYALTY_FLASH_SALE_STOP_ISO'),
    warehouseSaleSKUs: findEnvVarArray(['warehouseSaleSKUs'], 'PUBLIC_WAREHOUSE_SALE_SKUS'),
    warehouseDiscountPercentage: findEnvVarNumber(['warehouseDiscountPercentage'], 'PUBLIC_WAREHOUSE_DISCOUNT_PERCENTAGE'),
    warehouseSaleEnabled: findEnvVarBoolean(['warehouseSaleEnabled'], 'PUBLIC_WAREHOUSE_SALE_ENABLED'),
    standardShippingHolidayCutOff: findEnvVarString(['standardShippingHolidayCutOff'], 'PUBLIC_STANDARD_SHIPPING_HOLIDAY_CUTOFF'),
    timedSaleStartISO,
    timedSaleStopISO,
    timedSaleDiscounts: findEnvVarObject(['timedSaleDiscounts'], 'PUBLIC_TIMED_DISCOUNTS') as { [handle in ProductHandle]: number },
    timedSaleActive: isInDateRange(timedSaleStartISO, timedSaleStopISO),
  },
});

export const assetPathFnState = atom<AssetPathFn>({
  key: Common.AssetPathFn,
  default: assetPathFn,
});

export const reviewPageFamily = atomFamily<number, ProductHandle>({
  key: Common.ReviewSettings,
  default: 1,
});

export const reviewFamily = atomFamily<HoneyloveReviewSet, { productId: string; page: number; itemsPerPage: number }>({
  key: Common.Reviews,
  default: {} as HoneyloveReviewSet,
});

export const preorderState = atom<Preorder[]>({
  key: Common.Preorders,
  default: [],
});

export const customerState = atom<CustomerViewModel>({
  key: Common.Customer,
  default: {
    id: '',
    totalUsdSpentLast365Days: 0,
    activities: [],
    totalPoints: 0,
    firstName: '',
    lastName: '',
    created: '',
    birthday: '',
    emails: [],
    tier: 0,
  } as CustomerViewModel,
});

export const customerIsFetchingState = atom<boolean>({ key: Common.CustomerFetchingState, default: true });

export const abTestState = atom<HoneyloveAbTests>({
  key: Common.AbTests,
  default: (typeof window !== 'undefined' && window?.Honeylove?.abTests?.initial) || ({} as HoneyloveAbTests),
});

// TODO: Calculate this for SSR
const initialWidth = typeof screen !== 'undefined' ? screen?.width : 375;
const initialIsMobile = initialWidth < 768;
const initialIsDesktop = initialWidth > 1024;
const initialIsTablet = !initialIsMobile && !initialIsDesktop;

export const deviceTypeState = atom<DeviceType>({
  key: Common.DeviceType,
  default: initialIsDesktop ? DeviceType.Desktop : initialIsTablet ? DeviceType.Tablet : DeviceType.Mobile,
});

export const marketingPromoState = atom<MarketingPromo>({
  key: Common.MarketingPromo,
  default: MarketingPromo.None,
});

export const collectionState = atom<ShopifyCollection>({
  key: Common.CollectionHandle,
  default:
    typeof window !== 'undefined'
      ? (window.Honeylove?.collection as ShopifyCollection)
      : { handle: CollectionHandle.All, description: 'All Products', title: 'All Products' },
});

const defaultCurrency = (typeof window !== 'undefined' && window?.Shopify) || {
  currency: { active: 'USD', rate: 1.0 },
};
// TODO currency: wire up graphql to support other currencies
export const shopifyState = atom<Shopify | undefined>({
  key: Common.Shopify,
  default: defaultCurrency,
  dangerouslyAllowMutability: true,
});

// TODO currency: wire up graphql to support other currencies
export const toViewPriceFnState = atom<(priceInCents: number, maximumFractionDigits?: number) => string>({
  key: Common.ToViewPriceFn,
  default: toViewPrice(defaultCurrency.currency),
});

export const smsPhoneStatusState = atom<{ success: boolean; message?: string } | undefined>({
  key: Common.SmsPhoneStatus,
  default: undefined,
});

export const selectionPreorderFamily = atomFamily<Preorder | undefined, SelectionTypeKey>({
  key: Selections.Preorder,
  default: undefined,
});

export const _selectionSubContextFamily = atomFamily<SubContext | undefined, SelectionTypeKey>({
  key: Selections.SubContext,
  default: undefined,
});

// this is for selector use only use selectionHandleFamily (the selector) instead
export const _selectionFitFamily = atomFamily<Fit | undefined, SelectionTypeKey>({
  key: Selections._Fit,
  default: getURLParam<Fit>('fit') ?? Fit.Regular,
});

export const _selectionConfirmedFamily = atomFamily<boolean, SelectionTypeKey>({
  key: Selections._ConfirmedForBundleStepper,
  default: false,
});

const defaultSelectionHandleFamily = (key: SelectionTypeKey): ProductHandle => {
  if (key.selectionType === SelectionType.BundleProductSection) {
    const productHandle = key.key.split('_')?.[1] as ProductHandle;
    if (isProductHandle(productHandle)) return productHandle;
  }

  return (typeof window !== 'undefined' && window?.Honeylove?.activeProductHandle) || ProductHandle.SuperPowerShort;
};

// TODO set product handle for SSR
// this is for selector use only use selectionHandleFamily (the selector) instead
export const _selectionHandleFamily = atomFamily<ProductHandle, SelectionTypeKey>({
  key: Selections._Handle,
  default: (key) => defaultSelectionHandleFamily(key),
});

// this is for selector use only use selectionSizeFamily (the selector) instead
export const _selectionSizeFamily = atomFamily<LongSize | undefined, SelectionTypeKey>({
  key: Selections._Size,
  default: getURLParam<LongSize>('size') ?? undefined,
});

const defaultSelectionColorFamily = ({ key, selectionType }: SelectionTypeKey): Color | undefined => {
  if (selectionType === SelectionType.BundleProductSection) {
    const keys = key.split('_');

    const bundleCollectionHandle = keys?.[0] as BundleCollectionHandle;
    if (!isBundleCollectionHandle(bundleCollectionHandle)) return undefined;

    const productHandle = keys?.[1] as ProductHandle;
    if (!isProductHandle(productHandle)) return undefined;

    const index = +keys?.[2] as number;
    if (isNaN(index)) return undefined;

    const urlSwatchColor = getURLParam<string>('bundleColor');
    const config = bundleConfiguration[bundleCollectionHandle];
    const swatchTitle = config.productColorCombos.find((pcc) => pcc.swatchTitle === urlSwatchColor)?.swatchTitle ?? config.productColorCombos[0].swatchTitle;
    const productColorCombo = config.productColorCombos.find((pcc) => pcc.swatchTitle === swatchTitle);
    const color = productColorCombo?.colorCombos[index].color;
    return color;
  }

  return selectionType === SelectionType.CartPromo ? undefined : getURLParam<Color>('color') ?? undefined;
};

// this is for selector use only use selectionColorFamily (the selector) instead
export const _selectionColorFamily = atomFamily<Color | undefined, SelectionTypeKey>({
  key: Selections._Color,
  default: (key) => defaultSelectionColorFamily(key),
});

// this is for selector use only use selectionColorFamily (the selector) instead
export const _selectionDenominationFamily = atomFamily<Denomination | undefined, SelectionTypeKey>({
  key: Selections._Denomination,
  default: undefined,
});

export const _bundleCollectionSwatchAtom = atomFamily<string, BundleCollectionHandle>({
  key: Common.BundleCollectionSwatch,
  default: (collectionHandle) => {
    const config = bundleConfiguration[collectionHandle];

    const urlSwatchColor = getURLParam<string>('bundleColor');
    const swatchTitle = config.productColorCombos.find((pcc) => pcc.swatchTitle === urlSwatchColor)?.swatchTitle ?? config.productColorCombos[0].swatchTitle;

    return swatchTitle;
  },
});

// this is for selector use only use sizingToolState (the selector) instead
export const _sizingToolState = atom<SizingToolStateProps>({
  key: Selections._SizingToolState,
  default: {
    sizingToolState: SizingToolState.Closed,
    skipTracking: false,
    selectionType: SelectionType.ProductSection,
    selectionKey: '0',
    initialProduct: undefined,
    product: undefined,
  },
});

// this is for selector use only use modalState (the selector) instead
export const _modalState = atom<ModalState>({
  key: Selections._ModalState,
  default: {
    toggleState: ModalToggleState.Closed,
  },
});

// this is for selector use only use cartDrawerState (the selector) instead
export const _cartDrawerState = atom<DrawerState>({
  key: Selections._CartDrawer,
  default: {
    toggleState: DrawerToggleState.Closed,
  },
});

// this is for selector use only use cartState (the selector) instead
export const _cartState = atom<ShopifyCart>({
  key: Selections._Cart,
  default: {} as ShopifyCart,
});

// this is for selector use only use cartState (the selector) instead
export const mobileMenuOpenState = atom<boolean>({
  key: Common.MobileMenuOpen,
  default: false,
});

// this is for selector use only use selectionKlaviyoResponseFamily (the selector) instead
export const _selectionKlaviyoResponseFamily = atomFamily<ReceiveKlaviyoResponsePayload | undefined, string>({
  key: Selections._KlaviyoResponse,
  default: undefined,
});

export const appliedDiscountCodeState = atom<string | undefined>({
  key: Common.AppliedDiscountCode,
  default: undefined,
});

export const activeNavCategorySetState = atom<NavCategorySet>({
  key: Common.ActiveCategory,
  default: allNavCategorySet,
});

export const activeCollectionFilterState = atom<CollectionFilter | undefined>({
  key: Common.ActiveCollectionFilter,
  default: undefined,
});

export const appliedCollectionFilterState = atom<AppliedFilters>({
  key: Common.CollectionFilterState,
  default: {
    size: [],
    color: [],
    feature: [],
    sort: [],
  },
});

export const collectionSelectedVariantFamily = atomFamily<ViewVariant, ProductHandle>({
  key: Common.CollectionSelectedVariantFamily,
  default: selectorFamily({
    key: Common.CollectionSelectedVariantFamilyDefault,
    get:
      (handle) =>
      ({ get }) => {
        const products = get(allProductsState);
        const product = products[handle];
        const appliedFilters = get(appliedCollectionFilterState);
        const initialVariant = product.variants.find((v) => v.size === SIZE_TO_FILTER_BY && appliedFilters.color.includes(v.color)) ?? product.variants[0];
        return initialVariant;
      },
  }),
});
